import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { Table, Typography, Select, Flex, Divider, Space } from "antd"
import { useTranslation } from "react-i18next"
import styles from "./index.module.css"
import { spacings } from "themes/variables"
import { Icons } from "assets/icons"
import { Text } from "ui/Typography"
import ExternalLink from "ui/Icon/DSIcon/ExternalLink"
import { generateTripLink, generateBookingLink } from "./utils"
import { useDevice } from "hooks/useDevice"
import Separator from "ui/Separator"

const FullySubstitutable = ({ upcomingBookings = [], assignedExperiences = [], list = [], warning, onSelectionsChange }) => {
  const { t } = useTranslation()
  const { isMobile } = useDevice()
  const [selections, setSelections] = useState({})
  const hasUpcoming = upcomingBookings.length > 0
  const hasAssigned = assignedExperiences.length > 0 || list.length > 0

  // Notify parent when selections change
  useEffect(() => {
    if (onSelectionsChange) {
      const allSelected = upcomingBookings.length > 0 && upcomingBookings.every((_, idx) => selections[idx] !== undefined)
      onSelectionsChange(selections, allSelected)
    }
  }, [selections, onSelectionsChange, upcomingBookings])

  const handleSelectChange = (bookingIndex, boatId) => {
    setSelections((prev) => ({
      ...prev,
      [bookingIndex]: boatId
    }))
  }

  return (
    <>
      {hasUpcoming && (
        <div className={`${styles.upcomingBookingsWrap} ${styles.mb16}`}>
          <div className={styles.upcomingBookingsHeader}>
            {t("equipment.modals.upcoming_bookings")} ({upcomingBookings.length})
          </div>
          {isMobile ? (
            <Flex vertical className={`${styles.mobileBookingsList} px-m`}>
              {upcomingBookings.map((record, index) => {
                const experienceName = record.experience_name || record.experienceName
                const bookingLink = generateBookingLink(record)
                const substitutes = record?.available_substitutes || []
                const options = substitutes.map((substitute) => {
                  let displayName
                  if (substitute.asset_number) {
                    displayName = `Asset #${substitute.asset_number} (${substitute.boat_name})`
                  } else if (substitute.available_assets) {
                    displayName = `${substitute.boat_name} (${t("equipment.modals.available")}: ${substitute.available_assets})`
                  } else {
                    displayName = substitute.boat_name
                  }
                  return {
                    label: displayName,
                    value: substitute.boat_asset_id || substitute.boat_id
                  }
                })
                return (
                  <Flex vertical key={index} className={styles.mobileBookingCard}>
                    <div>
                      <Typography.Link href={bookingLink} target="_blank" rel="noopener noreferrer" className={styles.externalLink}>
                        {experienceName} <ExternalLink className="ml-xxs" />
                      </Typography.Link>
                    </div>
                    <Flex align="center" wrap gap={10}>
                      <Flex align="center" className={styles.mobileBookingRow}>
                        {record.booked_date} <Separator /> {record.client_name || record.clientName}
                      </Flex>
                      <Separator className={styles.hideOnMobile}/>
                      <Flex align="center" className={styles.mobileBookingRow}>
                        {record.start_time && record.end_time ? `${record.start_time} - ${record.end_time}` : record.time || "-"}
                        <Separator />
                        <Space>ID: {record.booking_id || record.booking_uuid || record.id}</Space>
                      </Flex>
                    </Flex>

                    <div className={styles.mobileBookingRow}>
                      {!Array.isArray(substitutes) || substitutes.length === 0 ? (
                        <Typography.Text className={styles.mobileBookingSelect}>-</Typography.Text>
                      ) : (
                        <Select
                          placeholder={t("equipment.modals.select_replacement")}
                          style={{ width: "100%" }}
                          options={options}
                          value={selections[index]}
                          onChange={(value) => handleSelectChange(index, value)}
                          className={styles.mobileBookingSelect}
                        />
                      )}
                    </div>
                  </Flex>
                )
              })}
            </Flex>
          ) : (
            <Table
              className={styles.upcomingBookingsTable}
              dataSource={upcomingBookings.map((item, idx) => ({ ...item, key: idx }))}
              pagination={false}
              bordered={false}
              columns={[
                {
                  title: t("equipment.modals.table.experience_name"),
                  key: "experienceName",
                  render: (_, record) => {
                    const experienceName = record.experience_name || record.experienceName
                    const bookingLink = generateBookingLink(record)
                    return (
                      <Flex gap={spacings.XS} align="center">
                        <Typography.Link href={bookingLink} target="_blank" rel="noopener noreferrer" className={styles.externalLink}>
                          {experienceName}
                          <ExternalLink className="ml-xxs" />
                        </Typography.Link>
                      </Flex>
                    )
                  }
                },
                {
                  title: t("equipment.modals.table.date"),
                  key: "date",
                  render: (_, record) => record.booked_date
                },
                {
                  title: t("equipment.modals.table.time"),
                  key: "time",
                  render: (_, record) => {
                    if (record.start_time && record.end_time) {
                      return `${record.start_time} - ${record.end_time}`
                    }
                    return record.time || "-"
                  }
                },
                {
                  title: t("equipment.modals.table.id"),
                  key: "id",
                  render: (_, record) => record.booking_id || record.booking_uuid || record.id
                },
                {
                  title: t("equipment.modals.table.client_name"),
                  key: "clientName",
                  render: (_, record) => record.client_name || record.clientName
                },
                {
                  title: t("equipment.modals.table.replace_with"),
                  key: "replaceWith",
                  render: (_, record, index) => {
                    const substitutes = record?.available_substitutes || []
                    if (!Array.isArray(substitutes) || substitutes.length === 0) {
                      return <Typography.Text>-</Typography.Text>
                    }
                    const options = substitutes.map((substitute) => {
                      let displayName
                      if (substitute.asset_number) {
                        displayName = `Asset #${substitute.asset_number} (${substitute.boat_name})`
                      } else if (substitute.available_assets) {
                        displayName = `${substitute.boat_name} (${t("equipment.modals.available")}: ${substitute.available_assets})`
                      } else {
                        displayName = substitute.boat_name
                      }
                      return {
                        label: displayName,
                        value: substitute.boat_asset_id || substitute.boat_id
                      }
                    })
                    return (
                      <Select
                        placeholder={t("equipment.modals.select_replacement")}
                        style={{ width: "100%" }}
                        options={options}
                        value={selections[index]}
                        onChange={(value) => handleSelectChange(index, value)}
                      />
                    )
                  }
                }
              ]}
            />
          )}
        </div>
      )}
      {hasAssigned && (
        <div className={styles.mt16}>
          <div className={styles.assignedExperiences}>
            {t("equipment.modals.assigned_to_experiences")} ({assignedExperiences.length || list.length})
          </div>
          {warning && (
            <Flex align="center" gap={spacings.XS} className={styles.warningAlert}>
              <Icons.OrangeAlertTriangle />
              <Text>{warning}</Text>
            </Flex>
          )}
          <div>
            {(assignedExperiences.length > 0 ? assignedExperiences : list).map((item, idx) => {
              const { trip_name, name, trip_id, status } = item || {}
              const tripName = trip_name || name
              const tripLink = generateTripLink({ trip_id, status })

              return (
                <>
                  <Flex key={tripName + idx} gap={spacings.XS} align="center">
                    <Typography.Link href={tripLink} target="_blank" rel="noopener noreferrer" className={styles.externalLink}>
                      {tripName}
                      <ExternalLink className="ml-xxs" />
                    </Typography.Link>
                  </Flex>
                  <Divider />
                </>
              )
            })}
          </div>
        </div>
      )}
    </>
  )
}

FullySubstitutable.propTypes = {
  upcomingBookings: PropTypes.array,
  assignedExperiences: PropTypes.array,
  list: PropTypes.array,
  warning: PropTypes.string,
  onSelectionsChange: PropTypes.func
}

export default FullySubstitutable
